// Il questionario demo. I valori sono quelli che il backend accetta (POST
// /api/demo-requests) e che finiscono nell'email e nel foglio; l'etichetta è
// la copia mostrata.
const option = (value, label = value) => ({ value, label });

export const OPTIONS = {
  fleetSize: ['1-10', '11-25', '26-50', '51-100', '100+'].map((v) => option(v)),
  vehicleTypes: [
    'Furgoni',
    'Auto operative',
    'Mezzi tecnici',
    'Noleggio',
    'EV / ibridi',
    'Flotta mista',
  ].map((v) => option(v)),
  coverage: [
    option('Una citta', 'Una città'),
    option('Piu citta', 'Più città'),
    option('Regionale'),
    option('Nazionale'),
  ],
  trackingState: [
    option('Tracking attivo su tutti i mezzi'),
    option('Tracking parziale'),
    option('Nessun tracking'),
  ],
  mainChallenges: [
    option('Visibilita live limitata', 'Visibilità live limitata'),
    option('Manutenzioni non pianificate'),
    option('Costi poco leggibili'),
    option('Documenti e scadenze sparse'),
    option('Alert troppo rumorosi'),
    option('Mezzi senza OBD da gestire'),
  ],
  requestedModules: [
    'Operations Room',
    'Tracking e tracciati',
    'Manutenzioni',
    'Carburante e pedaggi',
    'Documenti e scadenze',
    'Analytics / KPI',
  ].map((v) => option(v)),
  urgency: ['Subito', 'Entro 3 mesi', 'Entro 6 mesi', 'Valutazione in corso'].map((v) => option(v)),
  preferredContact: [
    'Call operativa',
    'Demo guidata',
    'Mail con proposta',
    'Workshop esplorativo',
  ].map((v) => option(v)),
};

const MULTI_FIELDS = ['vehicleTypes', 'mainChallenges', 'requestedModules'];
const TEXT_FIELDS = [
  'companyName',
  'fullName',
  'role',
  'email',
  'phone',
  'currentStack',
  'objective',
  'notes',
];

/** @public */
export const EMPTY_LEAD = {
  companyName: '',
  fullName: '',
  role: '',
  email: '',
  phone: '',
  fleetSize: '',
  vehicleTypes: [],
  coverage: '',
  trackingState: '',
  currentStack: '',
  mainChallenges: [],
  requestedModules: [],
  urgency: '',
  objective: '',
  preferredContact: '',
  notes: '',
  consent: false,
};

export const STEPS = [
  {
    label: 'Profilo',
    title: 'Chi siete e chi gestisce il progetto',
    description:
      'Raccogliamo i riferimenti principali per capire contesto aziendale, referente e perimetro iniziale.',
  },
  {
    label: 'Flotta',
    title: "Com'è composta la flotta oggi",
    description:
      'Ci serve una fotografia rapida di volumi, copertura e livello di tracking attuale.',
  },
  {
    label: 'Esigenze',
    title: 'Dove serve più controllo operativo',
    description:
      'Qui emergono i problemi reali: fermo veicolo, costi, documenti, alert e priorità operative.',
  },
  {
    label: 'Contatto',
    title: 'Come organizzare il prossimo passaggio',
    description:
      'Chiudiamo il brief con obiettivo, urgenza e formato migliore per la prossima call o demo.',
  },
];
export const LAST_STEP = STEPS.length - 1;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const blank = (text) => !text.trim();

const STEP_RULES = [
  [
    ['companyName', (l) => blank(l.companyName), 'Inserisci il nome azienda.'],
    ['fullName', (l) => blank(l.fullName), 'Inserisci nome e cognome.'],
    ['email', (l) => !EMAIL.test(l.email.trim()), 'Inserisci una mail valida.'],
  ],
  [
    ['fleetSize', (l) => !l.fleetSize, 'Seleziona una dimensione flotta.'],
    ['vehicleTypes', (l) => l.vehicleTypes.length === 0, 'Seleziona almeno una tipologia.'],
    ['coverage', (l) => !l.coverage, 'Indica la copertura geografica.'],
    ['trackingState', (l) => !l.trackingState, 'Indica lo stato del tracking.'],
  ],
  [
    [
      'mainChallenges',
      (l) => l.mainChallenges.length === 0,
      'Seleziona almeno un problema prioritario.',
    ],
    ['requestedModules', (l) => l.requestedModules.length === 0, 'Seleziona almeno un modulo.'],
    ['urgency', (l) => !l.urgency, "Indica l'urgenza del progetto."],
    ['currentStack', (l) => blank(l.currentStack), 'Descrivi gli strumenti o il processo attuale.'],
  ],
  [
    ['objective', (l) => blank(l.objective), "Descrivi l'obiettivo principale."],
    ['preferredContact', (l) => !l.preferredContact, 'Scegli il prossimo step preferito.'],
    ['consent', (l) => !l.consent, 'Conferma il consenso per poter essere ricontattato.'],
  ],
];

/** Gli errori di uno step, per campo; vuoto se si può andare avanti. */
export function validateStep(step, lead) {
  return Object.fromEntries(
    STEP_RULES[step]
      .filter(([, fails]) => fails(lead))
      .map(([field, , message]) => [field, message])
  );
}

const known = (field, value) => OPTIONS[field].some((o) => o.value === value);

/**
 * La bozza salvata nel browser, ripulita: campi mancanti vuoti e scelte che il
 * modulo non offre più tolte. Una bozza illeggibile riparte da zero.
 */
export function restoreDraft(saved) {
  let draft;
  try {
    draft = JSON.parse(saved);
  } catch {
    return EMPTY_LEAD;
  }
  if (!draft || typeof draft !== 'object') return EMPTY_LEAD;
  const lead = { ...EMPTY_LEAD };
  for (const field of TEXT_FIELDS) {
    if (typeof draft[field] === 'string') lead[field] = draft[field];
  }
  for (const field of ['fleetSize', 'coverage', 'trackingState', 'urgency', 'preferredContact']) {
    if (known(field, draft[field])) lead[field] = draft[field];
  }
  for (const field of MULTI_FIELDS) {
    if (Array.isArray(draft[field])) lead[field] = draft[field].filter((v) => known(field, v));
  }
  lead.consent = draft.consent === true;
  return lead;
}

/** Scelta singola: la nuova sostituisce la precedente. Multipla: una seconda scelta la toglie. */
export function choose(lead, field, value) {
  if (!MULTI_FIELDS.includes(field)) return { ...lead, [field]: value };
  const values = lead[field];
  return {
    ...lead,
    [field]: values.includes(value) ? values.filter((v) => v !== value) : [...values, value],
  };
}

const labelOf = (field, value) => OPTIONS[field].find((o) => o.value === value)?.label;
const labelsOf = (field, values) => values.map((v) => labelOf(field, v)).join(', ');

/** Il riepilogo a lato del modulo mentre si compila. */
export function draftSnapshot(lead) {
  return [
    ['Azienda', lead.companyName || 'Non indicata'],
    ['Referente', lead.fullName || 'Non indicato'],
    ['Flotta', labelOf('fleetSize', lead.fleetSize) ?? 'Da definire'],
    ['Copertura', labelOf('coverage', lead.coverage) ?? 'Da definire'],
    ['Tracking', labelOf('trackingState', lead.trackingState) ?? 'Da definire'],
    ['Urgenza', labelOf('urgency', lead.urgency) ?? 'Da definire'],
  ];
}

/** Il riepilogo della richiesta inviata. */
export function submittedSummary(lead) {
  return [
    ['Azienda', lead.companyName],
    ['Referente', lead.fullName],
    ['Flotta', labelOf('fleetSize', lead.fleetSize)],
    ['Tracking', labelOf('trackingState', lead.trackingState)],
    ['Esigenze', labelsOf('mainChallenges', lead.mainChallenges)],
    ['Moduli', labelsOf('requestedModules', lead.requestedModules)],
    ['Obiettivo', lead.objective],
  ];
}

/** Il testo di un invio fallito: il messaggio del server resta per il debug. */
export function submitErrorMessage(error) {
  return error?.response?.status === 429
    ? 'Troppe richieste. Riprova tra qualche minuto.'
    : 'Invio non riuscito. Riprova tra poco.';
}
